/**
 * Derivations behind the Payroll Sync Status card (KAN-679): the sync report
 * and the active sync's stored data (KAN-678 `sync-history`) turned into view
 * models the card and its detail components render as-is.
 *
 * Pure functions, no React and no state, so the card stays declarative and
 * every rule here is testable without rendering. `UserController` calls
 * `buildPayrollSyncStatusView` from the page; nothing below reads `this`.
 */
import { formatMoney } from '../../../utils/money';
import { formatDateWithZone, formatStoredValue, formatTimestampWithZone } from '../../../utils/datetime';
import { describeSyncReport } from '../../reports/elements/syncStatus.constants';
import { SYNC_DETAIL_ERROR, SYNC_DETAIL_STATUS } from '../../reports/reports.constants';
import {
    PAYROLL_SYNC_CARD_COPY,
    SYNC_ATTRIBUTION,
    SYNC_ATTRIBUTION_COPY,
    SYNC_EMPTY_VALUE,
    SYNC_FIELD_LABELS,
    SYNC_HISTORY_COPY,
    SYNC_HISTORY_DIMENSIONS,
    SYNC_MONEY_KEYS,
    SYNC_MONEY_KEY_SUFFIX,
} from './elements/syncHistory/syncHistory.constants';

const MONEY_KEYS = new Set(SYNC_MONEY_KEYS);

/** Attribution sentence under a dimension, from the endpoint's own field. */
export function describeAttribution(attribution) {
    return SYNC_ATTRIBUTION_COPY[attribution] || SYNC_HISTORY_COPY.UNASSIGNED;
}

/** "3 stored during this sync"; only ever built for `attribution: window`. */
export function describeStoredCount(count, attribution) {
    if (attribution !== SYNC_ATTRIBUTION.WINDOW) {
        return SYNC_HISTORY_COPY.UNASSIGNED;
    }
    return `${Number(count) || 0} stored during this sync`;
}

/** "Showing the last 5 of 12", or null when everything stored is on screen. */
export function describeRecordsSlice(shown, count) {
    const total = Number(count) || 0;
    if (total <= shown) return null;
    return `Showing the last ${shown} of ${total}`;
}

export function isMoneyKey(key) {
    return MONEY_KEYS.has(key) || SYNC_MONEY_KEY_SUFFIX.test(String(key));
}

/** Cents → `$1,234.56`; anything non-numeric is returned untouched. */
export function formatStoredMoney(cents) {
    const numeric = Number(cents);
    if (cents === null || cents === undefined || cents === '' || !Number.isFinite(numeric)) return cents;
    return formatMoney(numeric / 100);
}

/** `id` and `*_id`: row identifiers, never shown. The service strips them too. */
export function isIdentifierKey(key) {
    return key === 'id' || /_id$/.test(String(key));
}

export function formatFieldLabel(key) {
    return SYNC_FIELD_LABELS[key] || String(key).replace(/_/g, ' ');
}

const isNestedValue = (value) => value !== null && typeof value === 'object';

/**
 * Order a record's entries for display: `order` first (in that order), then
 * unlisted scalars as they arrived, then nested blocks as they arrived.
 * @param {Array<[string, unknown]>} entries
 * @param {Array<string>} [order]
 */
export function orderRecordEntries(entries, order = []) {
    const rank = new Map(order.map((key, index) => [key, index]));
    const listed = entries.filter(([key]) => rank.has(key)).sort(([a], [b]) => rank.get(a) - rank.get(b));
    const unlisted = entries.filter(([key]) => !rank.has(key));
    return [
        ...listed,
        ...unlisted.filter(([, value]) => !isNestedValue(value)),
        ...unlisted.filter(([, value]) => isNestedValue(value)),
    ];
}

/**
 * The stored direct-deposit allocation has both `type` and `account_type`
 * for the same "checking / savings" fact, filled by different writers
 * (the payroll webhook writes `type`, mOCR and Atomic write `account_type`).
 * One row: `account_type`, falling back to `type`.
 */
export function unifyAllocationAccountType(allocation) {
    if (!allocation || typeof allocation !== 'object') return allocation;
    const { type, account_type, ...rest } = allocation;
    return { ...rest, account_type: account_type ?? type ?? null };
}

function presentScalar(key, value, timeZone) {
    if (isMoneyKey(key)) return formatStoredMoney(value);
    return formatStoredValue(value, timeZone);
}

function presentNested(value, timeZone) {
    if (Array.isArray(value)) return value.map((item) => presentNested(item, timeZone));
    if (!isNestedValue(value)) return value;
    return Object.fromEntries(
        Object.entries(value)
            .filter(([key]) => !isIdentifierKey(key))
            .map(([key, inner]) => [key, isNestedValue(inner) ? presentNested(inner, timeZone) : presentScalar(key, inner, timeZone)]),
    );
}

/**
 * A stored JSON record as label / value rows. Provider payloads have no fixed
 * schema, so nothing is hand-mapped: scalars are a row each, nested values a
 * formatted JSON block. Identifier keys are dropped; ISO dates and timestamps
 * render in `timeZone`; cent amounts render as dollars, inside nested JSON too.
 *
 * @param {Object | null | undefined} record
 * @param {{ order?: Array<string>, timeZone?: string | null }} [options]
 * @returns {Array<{ key: string, label: string, value: string, nested: boolean }>}
 */
export function buildRecordFields(record, { order = [], timeZone = null } = {}) {
    if (!record || typeof record !== 'object') return [];
    const entries = orderRecordEntries(
        Object.entries(record).filter(([key]) => !isIdentifierKey(key)),
        order,
    );
    return entries.map(([key, value]) => {
        const nested = isNestedValue(value);
        let text;
        if (nested) {
            text = JSON.stringify(presentNested(value, timeZone), null, 2);
        } else if (value === null || value === undefined || value === '') {
            text = SYNC_EMPTY_VALUE;
        } else {
            text = String(presentScalar(key, value, timeZone));
        }
        return { key, label: formatFieldLabel(key), value: text, nested };
    });
}

function buildRecordDetail(entry, label, shape, timeZone) {
    let note = null;
    let fields = [];
    if (entry.masked) {
        note = SYNC_HISTORY_COPY.MASKED;
    } else if (entry.record) {
        fields = buildRecordFields(entry.record, { order: shape.fieldOrder, timeZone });
    } else {
        note = SYNC_HISTORY_COPY.NO_RECORD;
    }
    return {
        kind: 'record',
        meta: {
            label: SYNC_HISTORY_COPY.STORED_DURING_THIS_SYNC,
            value: entry.stored_during_this_sync ? SYNC_HISTORY_COPY.YES : SYNC_HISTORY_COPY.NO,
        },
        hint: describeAttribution(entry.attribution),
        note,
        fields,
        fieldsLabel: `${label} record`,
    };
}

function buildStoredRecordsDetail(entry, label, shape, timeZone) {
    const records = Array.isArray(entry.records) ? entry.records : [];
    const formatRecordedAt = shape.recordedAtIsDate ? formatDateWithZone : formatTimestampWithZone;
    return {
        kind: 'stored',
        countText: describeStoredCount(entry.stored_count, entry.attribution),
        hint: describeAttribution(entry.attribution),
        sliceText: records.length ? describeRecordsSlice(records.length, entry.stored_count) : null,
        listLabel: `Last ${label.toLowerCase()} stored`,
        records: records.map((record) => ({
            summary: `${shape.recordedAtLabel} ${formatRecordedAt(record?.recorded_at)}`,
            fields: buildRecordFields(record?.data, { order: shape.fieldOrder, timeZone }),
        })),
    };
}

function buildAllocationDetail(entry, shape, timeZone) {
    const allocation = entry.allocation;
    const view = {
        kind: 'allocation',
        note: describeAttribution(entry.attribution ?? SYNC_ATTRIBUTION.ACCOUNT),
        emptyNote: allocation ? null : SYNC_HISTORY_COPY.NO_ALLOCATION,
        allocations: [],
        fields: [],
    };
    if (!allocation || typeof allocation !== 'object') return view;

    const { allocations, ...rest } = allocation;
    view.allocations = Array.isArray(allocations)
        ? allocations.map((item) => buildRecordFields(unifyAllocationAccountType(item), { order: shape.allocationFieldOrder, timeZone }))
        : [];
    view.fields = buildRecordFields(unifyAllocationAccountType(rest), { order: shape.fieldOrder, timeZone });
    return view;
}

/**
 * The stored data behind one dimension of the active sync, ready to render.
 * `kind` picks the body: `unassigned`, `record`, `stored` or `allocation`.
 *
 * @param {{ dimensionKey: string, label: string, entry: Object | null | undefined, timeZone?: string | null }} p
 * @returns {Object}
 */
export function buildStoredDimensionView({ dimensionKey, label, entry, timeZone = null }) {
    const errorMessage = entry?.error_message || null;
    const shape = SYNC_HISTORY_DIMENSIONS[dimensionKey];
    if (!entry || entry.attribution === null || entry.attribution === undefined || !shape) {
        return { kind: 'unassigned', errorMessage, note: SYNC_HISTORY_COPY.UNASSIGNED };
    }
    switch (shape.kind) {
        case 'record':
            return { errorMessage, ...buildRecordDetail(entry, label, shape, timeZone) };
        case 'stored':
            return { errorMessage, ...buildStoredRecordsDetail(entry, label, shape, timeZone) };
        case 'allocation':
            return { errorMessage, ...buildAllocationDetail(entry, shape, timeZone) };
        default:
            return { kind: 'unassigned', errorMessage, note: SYNC_HISTORY_COPY.UNASSIGNED };
    }
}

/**
 * Everything the Payroll Sync Status card renders, from the page's state.
 *
 * `detail` is the `{ status, report, error }` SyncDetail the Reports panel
 * also uses. While reloading, the controller keeps the previous report on it,
 * so the pills stay put instead of flashing "Unavailable"; the first load (no
 * report yet) gets its own loading pill, never the "could not be loaded" one.
 *
 * `stored` is null when this admin may not see stored data; otherwise its
 * `entry` is `syncs[0]` (the same row the pills describe) and each dimension
 * row expands to what that sync stored. `openDimensions` holds which rows are
 * expanded; only rows with stored data can be.
 *
 * @param {{
 *   detail: Object | null,
 *   stored?: { loading?: boolean, error?: Object | null, entry?: Object | null, timeZone?: string | null } | null,
 *   openDimensions?: Object<string, boolean>,
 * }} p
 */
export function buildPayrollSyncStatusView({ detail, stored = null, openDimensions = {} }) {
    const loading = detail?.status === SYNC_DETAIL_STATUS.LOADING;
    const failed = detail?.status === SYNC_DETAIL_STATUS.ERROR;
    const report = detail?.status === SYNC_DETAIL_STATUS.READY || loading ? detail?.report ?? null : null;
    const described = describeSyncReport(report);
    const rollup = loading && !report ? PAYROLL_SYNC_CARD_COPY.LOADING_ROLLUP : described.rollup;
    const storedEntry = stored?.entry ?? null;

    let errorText = null;
    if (failed) {
        errorText = (detail.error || SYNC_DETAIL_ERROR.LOAD_FAILED) === SYNC_DETAIL_ERROR.TIMEOUT
            ? PAYROLL_SYNC_CARD_COPY.LOAD_TIMEOUT
            : PAYROLL_SYNC_CARD_COPY.LOAD_FAILED;
    }

    const rows = report
        ? described.dimensions.map(({ key, label, state }) => {
            const expandable = !!storedEntry;
            const expanded = expandable && !!openDimensions[key];
            return {
                key,
                label,
                state,
                stateLabel: `${label}: ${state.label}`,
                expandable,
                expanded,
                toggleLabel: `${expanded ? 'Collapse' : 'Expand'} ${label}`,
                detail: expanded
                    ? buildStoredDimensionView({
                        dimensionKey: key,
                        label,
                        entry: storedEntry.dimensions?.[key],
                        timeZone: stored?.timeZone ?? null,
                    })
                    : null,
            };
        })
        : [];

    // Under the rows, only while the stored data has not arrived: a load in
    // flight, or a failed read the admin can retry.
    const storedPending = !!report && !!stored && !storedEntry;
    return {
        rollup: {
            label: rollup.label,
            tone: rollup.tone,
            tooltip: described.inconsistency ? `${rollup.description} ${described.inconsistency}` : rollup.description,
            ariaLabel: `Sync status: ${rollup.label}`,
        },
        inconsistency: described.inconsistency,
        loadingText: loading ? PAYROLL_SYNC_CARD_COPY.LOADING_DIMENSIONS : null,
        errorText,
        rows,
        storedLoadingText: storedPending && stored.loading ? SYNC_HISTORY_COPY.LOADING : null,
        storedErrorText: storedPending && stored.error ? SYNC_HISTORY_COPY.ERROR : null,
    };
}
